/**
 * Human-readable save-slot labelling (pure, unit tested). Used by the title 继续旅程 picker and the
 * pause menu save/load panel.
 */
import type { SaveData, SaveSlot } from './schema';
import type { StoryFlagId } from '@/story/flags';

/** Chapter beat name shown for the highest flag reached. Wording follows docs/STORY_ADAPTATION.md. */
export const BEAT_NAMES: Record<StoryFlagId, string> = {
  TITLE: '序章',
  LETTER_OPEN: '一封家书',
  MEMORY_STORM: '黑夜风浪',
  MEMORY_ISLAND_RITUAL: '无名小岛',
  MEMORY_BLACK_WATER: '星塘黑水',
  MEMORY_FAREWELL: '冬日告别',
  MEMORY_SWORD_TRAINING: '路途剑术',
  LETTER_SENT: '封信入瓶',
  PROPHECY_REVEAL: '老者的思索',
  HARBOR_FREE_ROAM: '绿色码头',
  MERCHANT_TURTLE_EGG: '亚北商贩',
  RAIN_APPROACHES: '雨将至',
  INN_ARGUMENT: '大海龟旅店',
  OMEN_END: '不祥预感',
};

export const SLOT_NAMES: Record<SaveSlot, string> = { auto: '自动存档', 'manual-1': '存档一' };

export function beatName(flag: string | null | undefined): string {
  if (!flag) return '旅程之初';
  return (BEAT_NAMES as Record<string, string>)[flag] ?? flag;
}

/** Highest flag in a snapshot (flags are a route prefix, so the last entry). */
export function lastFlag(data: SaveData | null): string | null {
  if (!data) return null;
  const f = data.story.flags;
  return f.length ? (f[f.length - 1] ?? null) : null;
}

export function formatPlaytime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 60000));
  if (total < 1) return '不足1分钟';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}分钟`;
  return m === 0 ? `${h}小时` : `${h}小时${m}分`;
}

function pad2(n: number): string { return n < 10 ? `0${n}` : String(n); }

/** Local-time `YYYY-MM-DD HH:mm`; 0/invalid → '—'. */
export function formatTimestamp(ms: number, now: Date = new Date(ms)): string {
  if (!ms || !Number.isFinite(ms)) return '—';
  const d = now;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export interface SlotSummary {
  slot: SaveSlot;
  name: string;
  empty: boolean;
  beat: string;
  playtime: string;
  timestamp: string;
  /** One-line detail for narrow layouts. */
  detail: string;
}

export function describeSlot(slot: SaveSlot, data: SaveData | null): SlotSummary {
  const name = SLOT_NAMES[slot];
  if (!data) return { slot, name, empty: true, beat: '—— 空 ——', playtime: '', timestamp: '', detail: '尚无记录' };
  const beat = beatName(lastFlag(data));
  const playtime = formatPlaytime(data.playtimeMs);
  const timestamp = formatTimestamp(data.updatedAt);
  return { slot, name, empty: false, beat, playtime, timestamp, detail: `${playtime} · ${timestamp}` };
}
